// Shared viewport / mobile-mode detection for components that swap to a mobile
// layout below a breakpoint (currently the date & date-range pickers, which turn
// their anchored popup into a slide-up bottom sheet on phones).
//
// The breakpoint defaults to the --ebiz-breakpoint-mobile custom property defined in
// wwwroot/css/tokens.css (the design-token layer), so JS and CSS share one source of
// truth. Falls back to 768px if the stylesheet isn't loaded. A component that switches at
// a different width names its own token instead — see ebizResolveBreakpoint.
//
// One MediaQueryList is registered per component instance, keyed by the instance's
// UniqueId, mirroring the per-instance Map pattern used by drpOutsideClose in
// daterangepicker.js. matchMedia's 'change' event already fires on both resize and
// orientationchange, so no separate resize listener is needed.
window.ebizViewportWatchers = window.ebizViewportWatchers || new Map();

// Reads a CSS custom property off :root, or '' when it isn't defined.
function ebizCssLength(name) {
    return getComputedStyle(document.documentElement).getPropertyValue(name).trim();
}

// Reads a quoted CSS custom property (e.g. a media query string stashed in tokens.css) and strips
// the surrounding quotes, falling back when the property isn't defined.
function ebizCssQuery(name, fallback) {
    const raw = ebizCssLength(name);
    return raw ? raw.replace(/^["']|["']$/g, '') : fallback;
}

// Works out the width a watcher should switch at. `maxWidth` may be:
//   omitted            — the shared --ebiz-breakpoint-mobile token (768px)
//   '--some-token'     — that custom property, falling back to the shared one if it isn't defined
//   640 or '640px'     — an explicit width
// So the shared token stays the default while a component that switches somewhere else can name its
// own token and keep its breakpoint in CSS with the rest of them.
function ebizResolveBreakpoint(maxWidth) {
    const shared = () => ebizCssLength('--ebiz-breakpoint-mobile') || '768px';

    if (typeof maxWidth === 'number') {
        return `${maxWidth}px`;
    }

    if (typeof maxWidth === 'string' && maxWidth.trim()) {
        const value = maxWidth.trim();
        return value.startsWith('--') ? (ebizCssLength(value) || shared()) : value;
    }

    return shared();
}

// Registers a watcher for the given instance and immediately pushes the current
// match state to .NET. `method` is the [JSInvokable] name to call with a bool.
// `maxWidth` picks the breakpoint — see ebizResolveBreakpoint.
window.registerViewportWatcher = function (dotNetRef, key, method = 'SetIsMobile', maxWidth = null) {
    // Replace any existing watcher for this key so repeated first-render calls
    // don't stack listeners.
    window.disposeViewportWatcher(key);

    const mql = window.matchMedia(`(max-width: ${ebizResolveBreakpoint(maxWidth)})`);

    const notify = (matches) => {
        // Swallow errors from a disposed circuit / navigated-away page.
        dotNetRef.invokeMethodAsync(method, matches).catch(() => { });
    };

    const listener = (e) => notify(e.matches);

    // addEventListener('change') is supported in all evergreen browsers and modern WebViews.
    mql.addEventListener('change', listener);

    window.ebizViewportWatchers.set(key, { mql, listener });

    // Push the initial state so the component renders the correct layout on load.
    notify(mql.matches);
};

// Registers a per-instance watcher driven by a complete media *query* held in a CSS custom
// property, rather than by a width. For components whose mobile switch is a modality question
// instead of a space one — the side nav, which becomes an overlay panel because it is being driven
// by a finger, not because the viewport got narrow.
//
// Deliberately separate from registerPointerModeWatcher: that one is a single app-wide watcher
// behind EBizMediaQueryService (and so requires the service to be registered in DI), whereas this
// keeps the per-instance Map/key/teardown contract of registerViewportWatcher above, so
// disposeViewportWatcher works on it unchanged and no DI registration is needed.
window.registerQueryWatcher = function (dotNetRef, key, method = 'SetIsMobile', queryToken = '--ebiz-pointer-mode-query', fallbackQuery = '(pointer: coarse)') {
    // Replace any existing watcher for this key so repeated first-render calls don't stack.
    window.disposeViewportWatcher(key);

    const mql = window.matchMedia(ebizCssQuery(queryToken, fallbackQuery));

    const notify = (matches) => {
        // Swallow errors from a disposed circuit / navigated-away page.
        dotNetRef.invokeMethodAsync(method, matches).catch(() => { });
    };

    const listener = (e) => notify(e.matches);

    mql.addEventListener('change', listener);

    window.ebizViewportWatchers.set(key, { mql, listener });

    // Push the initial state so the component renders the correct layout on load.
    notify(mql.matches);
};

window.disposeViewportWatcher = function (key) {
    const entry = window.ebizViewportWatchers.get(key);
    if (!entry) {
        return;
    }

    const { mql, listener } = entry;
    mql.removeEventListener('change', listener);

    window.ebizViewportWatchers.delete(key);
};

// Pointer-mode watcher (touch vs. mouse/trackpad) backing EBizPointerModeService. Unlike the
// per-component width watchers above, this fires on the *input modality* tokens.css switches on
// (see its "Mobile mode" section) — a single global fact, so only one instance is ever needed for
// the whole app/circuit, keyed under a fixed key in the same watcher Map so it shares
// disposeViewportWatcher's teardown logic.
const EBIZ_POINTER_MODE_KEY = '__ebizPointerMode';

window.registerPointerModeWatcher = function (dotNetRef, method = 'SetIsCoarsePointer') {
    window.disposeViewportWatcher(EBIZ_POINTER_MODE_KEY);

    // Reads the query from tokens.css's --ebiz-pointer-mode-query so the condition lives in one
    // place; falls back to the current default if the stylesheet isn't loaded.
    const query = ebizCssQuery('--ebiz-pointer-mode-query', '(pointer: coarse)');
    const mql = window.matchMedia(query);

    const notify = (matches) => {
        dotNetRef.invokeMethodAsync(method, matches).catch(() => { });
    };

    const listener = (e) => notify(e.matches);

    mql.addEventListener('change', listener);
    window.ebizViewportWatchers.set(EBIZ_POINTER_MODE_KEY, { mql, listener });

    notify(mql.matches);
};

window.disposePointerModeWatcher = function () {
    window.disposeViewportWatcher(EBIZ_POINTER_MODE_KEY);
};

// Locks/unlocks background scrolling while a mobile sheet is open. Reference-counted
// so multiple simultaneously-open sheets don't clobber each other's release.
window.ebizScrollLockCount = window.ebizScrollLockCount || 0;

window.lockBodyScroll = function (lock) {
    if (lock) {
        window.ebizScrollLockCount++;
    } else if (window.ebizScrollLockCount > 0) {
        window.ebizScrollLockCount--;
    }

    document.body.style.overflow = window.ebizScrollLockCount > 0 ? 'hidden' : '';
};
